import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { useIsMobileDevice } from "../../hooks/useIsMobileDevice"

import * as Styled from "./L2RMenuWithChildren.styled"
import { isChildrenConfig, L2RMenuWithChildrenProps } from "./L2RMenuWithChildren.types"

export const L2RMenuWithChildren = ({ anchorEl, onClose, open, menuConfig, ...menuProps }: L2RMenuWithChildrenProps) => {
    const submenuPaperRef = useRef(null)
    const submenuPopoverActionRef = useRef(null)
    const submenuResizeObserverRef = useRef(null)
    const menuListRef = useRef(null)
    const closeTimeoutRef = useRef(null)
    const isMobile = useIsMobileDevice()

    const [submenu, setSubmenu] = useState({ anchorEl: null, itemId: null })

    const clearCloseTimeout = useCallback(() => {
        clearTimeout(closeTimeoutRef.current)
        closeTimeoutRef.current = null
    }, [])

    const closeSubmenu = useCallback(() => {
        setSubmenu({ anchorEl: null, itemId: null })
        clearCloseTimeout()
    }, [clearCloseTimeout])

    const scheduleTimeoutClose = useCallback(() => {
        if (!isMobile) {
            clearCloseTimeout()
            closeTimeoutRef.current = setTimeout(closeSubmenu, 250)
        }
    }, [clearCloseTimeout, closeSubmenu, isMobile])

    const handleMenuItemEnter = useCallback((event, item) => {
        clearCloseTimeout()
        if (item.children) {
            setSubmenu({ anchorEl: event.currentTarget, itemId: item.id })
        }
    }, [clearCloseTimeout])

    const handleMenuItemLeave = useCallback((event) => {
        const next = event.relatedTarget
        if (!(next instanceof Node && submenuPaperRef.current?.contains(next))) {
            scheduleTimeoutClose()
        }
    }, [scheduleTimeoutClose])

    const handleSubmenuLeave = useCallback((event) => {
        const next = event.relatedTarget
        if (!(next instanceof Node && menuListRef.current?.contains(next))) {
            scheduleTimeoutClose()
        }
    }, [scheduleTimeoutClose])

    useEffect(() => clearCloseTimeout, [clearCloseTimeout])

    // A callback ref (rather than an effect keyed on submenu.anchorEl) is required here:
    // the Popover's Paper mounts a render cycle after anchorEl becomes truthy, so an
    // effect depending on anchorEl would still see a null submenuPaperRef.current.
    const handleSubmenuPaperRef = useCallback((node) => {
        submenuPaperRef.current = node
        submenuResizeObserverRef.current?.disconnect()
        submenuResizeObserverRef.current = null
        if (node) {
            submenuResizeObserverRef.current = new ResizeObserver(() => {
                submenuPopoverActionRef.current?.updatePosition()
            })
            submenuResizeObserverRef.current.observe(node)
        }
    }, [])

    const handleMenuClose = useCallback((confirmed = true) => {
        if (isMobile) {
            closeSubmenu()
            onClose(confirmed)
            return
        }
        if (!submenu.anchorEl) {
            onClose(confirmed)
        }
    }, [isMobile, onClose, submenu.anchorEl, closeSubmenu])

    const augmentedMenuConfig = useMemo(() => menuConfig?.map((item) => ({
        ...item,
        hasChildren: Boolean(item.children),
        children: undefined,
        props: {
            onMouseEnter: (e) => handleMenuItemEnter(e, item),
            onMouseLeave: handleMenuItemLeave,
            ...item.props,
        },
    })), [menuConfig, handleMenuItemEnter, handleMenuItemLeave])

    const currentSubmenu = useMemo(
        () => menuConfig?.find((item) => submenu.itemId && item.id === submenu.itemId),
        [menuConfig, submenu.itemId],
    )
    const childrenConfig = isChildrenConfig(currentSubmenu?.children) ? currentSubmenu.children : null
    const Children = childrenConfig?.component ?? null

    return <>
        <Styled.Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleMenuClose}
            menuConfig={augmentedMenuConfig}
            MenuListProps={{ ref: menuListRef }}
            {...menuProps}
        />
        {!isMobile && Children && (
            <Styled.ContainerPopover
                action={submenuPopoverActionRef}
                anchorEl={submenu.anchorEl}
                open={Boolean(submenu.anchorEl)}
                onClose={closeSubmenu}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                disableAutoFocus
                disableEnforceFocus
                disableRestoreFocus
                style={{ pointerEvents: "none" }}
                slotProps={{
                    paper: {
                        ref: handleSubmenuPaperRef,
                        sx: { pointerEvents: "auto" },
                        onMouseEnter: clearCloseTimeout,
                        onMouseLeave: handleSubmenuLeave,
                    },
                }}
            >
                <Children currentSubmenu={currentSubmenu} onClose={handleMenuClose} {...childrenConfig?.props} />
            </Styled.ContainerPopover>
        )}
        {isMobile && Children && (
            <Children
                currentSubmenu={currentSubmenu}
                onClose={handleMenuClose}
                {...childrenConfig?.props}
            />
        )}
    </>
}